import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCreateActivity, useUpdateActivity } from '../hooks';
import type { Activity, ActivityType } from '../types';
import { RichTextEditor } from '../../components/RichTextEditor';

interface ActivityFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId?: string;
  companyId?: string;
  activity?: Activity | null;
  onSuccess: () => void;
}

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  NOTE: 'Notiz',
  CALL: 'Anruf',
  EMAIL: 'E-Mail',
  MEETING: 'Meeting',
  TASK: 'Aufgabe',
  FOLLOWUP: 'Follow-Up',
  APPOINTMENT: 'Termin',
  SOCIAL_MEDIA: 'Social Media',
  MESSENGER: 'Messenger',
  EVENT: 'Event – Teilnahme',
};

const ACTIVITY_TYPES: ActivityType[] = [
  'NOTE', 'CALL', 'EMAIL', 'MEETING', 'TASK',
  'FOLLOWUP', 'APPOINTMENT', 'SOCIAL_MEDIA', 'MESSENGER', 'EVENT',
];

const CALL_RESULTS = ['Erreicht', 'Mailbox', 'Nicht erreicht'];

function hasDynamicFields(type: ActivityType) {
  return type === 'CALL' || type === 'MEETING' || type === 'APPOINTMENT' || type === 'EVENT';
}

const label: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', fontWeight: 600,
  color: 'var(--color-text-muted)', marginBottom: '0.3rem',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

const input: React.CSSProperties = {
  width: '100%', padding: '0.5625rem 0.75rem',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
  fontSize: '0.9375rem', outline: 'none', boxSizing: 'border-box',
  background: 'var(--color-bg-card)', color: 'var(--color-text)',
};

export function ActivityFormModal({
  isOpen, onClose, contactId, companyId, activity, onSuccess,
}: ActivityFormModalProps) {
  const isEditMode = !!activity;
  const createMutation = useCreateActivity();
  const updateMutation = useUpdateActivity();
  const isSaveAndNew = useRef(false);

  const [error, setError] = useState('');
  const [type, setType] = useState<ActivityType>('NOTE');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [callResult, setCallResult] = useState('');
  const [duration, setDuration] = useState('');
  const [location, setLocation] = useState('');
  const [participants, setParticipants] = useState('');

  const resetForm = () => {
    setError('');
    setType('NOTE');
    setSubject('');
    setDescription('');
    setScheduledAt('');
    setCompletedAt('');
    setCallResult('');
    setDuration('');
    setLocation('');
    setParticipants('');
  };

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (activity) {
      setType(activity.type);
      setSubject(activity.subject);
      setDescription(activity.description ?? '');
      setScheduledAt(activity.scheduledAt ? activity.scheduledAt.slice(0, 16) : '');
      setCompletedAt(activity.completedAt ? activity.completedAt.slice(0, 16) : '');
      setCallResult('');
      setDuration('');
      setLocation('');
      setParticipants('');
    } else {
      resetForm();
    }
  }, [isOpen, activity]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const buildNotes = () => {
    const parts: string[] = [];
    if (type === 'CALL') {
      if (callResult) parts.push(`<strong>Ergebnis:</strong> ${callResult}`);
      if (duration) parts.push(`<strong>Dauer:</strong> ${duration} Min.`);
    } else if (type === 'MEETING' || type === 'APPOINTMENT' || type === 'EVENT') {
      if (location) parts.push(`<strong>Ort:</strong> ${location}`);
      if (participants) parts.push(`<strong>Teilnehmer:</strong> ${participants}`);
    }
    const metaHtml = parts.length ? `<p>${parts.join(' &nbsp;|&nbsp; ')}</p>` : '';
    if (!metaHtml && !description) return undefined;
    if (!metaHtml) return description;
    if (!description) return metaHtml;
    return `${metaHtml}${description}`;
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!subject.trim()) { setError('Betreff ist ein Pflichtfeld'); return; }

    const notes = buildNotes();

    const onError = (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Fehler beim Speichern';
      setError(msg);
    };

    if (isEditMode && activity) {
      updateMutation.mutate(
        {
          id: activity.id,
          data: {
            type, subject: subject.trim(),
            ...(notes ? { description: notes } : {}),
            ...(scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
            ...(completedAt ? { completedAt: new Date(completedAt).toISOString() } : {}),
          },
        },
        {
          onSuccess: () => onSuccess(),
          onError,
        },
      );
    } else {
      createMutation.mutate(
        {
          ...(contactId ? { contactId } : {}),
          ...(companyId ? { companyId } : {}),
          type, subject: subject.trim(),
          ...(notes ? { description: notes } : {}),
          ...(scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
          ...(completedAt ? { completedAt: new Date(completedAt).toISOString() } : {}),
        },
        {
          onSuccess: () => {
            if (isSaveAndNew.current) {
              isSaveAndNew.current = false;
              resetForm();
            } else {
              onSuccess();
            }
          },
          onError,
        },
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)', padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Aktivität bearbeiten' : 'Neue Aktivität'}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '900px', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          background: '#eff6ff',
          borderBottom: '1px solid #dbeafe',
          borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e3a8a' }}>
              {isEditMode ? 'Aktivität bearbeiten' : 'Neue Aktivität'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, fontSize: '1.375rem', lineHeight: 1,
              background: 'none', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: 'pointer', color: '#1e3a8a',
            }}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <form
          id="activity-form"
          onSubmit={handleSave}
          style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}
        >
          {error && (
            <div style={{
              padding: '0.625rem 0.875rem', marginBottom: '1rem',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 'var(--radius-sm)', color: 'var(--color-error)', fontSize: '0.875rem',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
            {/* Left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={label}>Typ</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as ActivityType)}
                  style={{ ...input, cursor: 'pointer' }}
                >
                  {ACTIVITY_TYPES.map((t) => (
                    <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={label}>Geplant am</label>
                <input
                  type="datetime-local"
                  style={input}
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </div>

              <div>
                <label style={label}>Erledigt am</label>
                <input
                  type="datetime-local"
                  style={input}
                  value={completedAt}
                  onChange={(e) => setCompletedAt(e.target.value)}
                />
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={label}>Betreff *</label>
                <input
                  style={input}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Betreff der Aktivität"
                  required
                />
              </div>

              {/* Dynamic fields box */}
              {hasDynamicFields(type) && (
                <div style={{
                  padding: '0.875rem 1rem',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                }}>
                  {type === 'CALL' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                      <div>
                        <label style={label}>Ergebnis</label>
                        <select
                          value={callResult}
                          onChange={(e) => setCallResult(e.target.value)}
                          style={{ ...input, cursor: 'pointer' }}
                        >
                          <option value="">— wählen —</option>
                          {CALL_RESULTS.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={label}>Dauer (Min)</label>
                        <input
                          type="number"
                          min={0}
                          style={input}
                          value={duration}
                          onChange={(e) => setDuration(e.target.value)}
                          placeholder="z.B. 15"
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                      <div>
                        <label style={label}>Ort / Messestand</label>
                        <input
                          style={input}
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          placeholder="z.B. Halle 3, Stand B12"
                        />
                      </div>
                      <div>
                        <label style={label}>Weitere Teilnehmer</label>
                        <input
                          style={input}
                          value={participants}
                          onChange={(e) => setParticipants(e.target.value)}
                          placeholder="z.B. Max Müller, Anna Schmidt"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label style={label}>Beschreibung</label>
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Notizen, Ergebnis, nächste Schritte..."
                  minHeight={180}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.875rem 1.5rem',
          borderTop: '1px solid var(--color-border)',
          flexShrink: 0, gap: '0.75rem',
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            style={{
              padding: '0.5rem 1.125rem', fontSize: '0.875rem',
              background: 'transparent', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              color: 'var(--color-text-muted)',
            }}
          >
            Abbrechen
          </button>

          <div style={{ display: 'flex', gap: '0.625rem' }}>
            {!isEditMode && (
              <button
                type="submit"
                form="activity-form"
                disabled={isPending}
                onClick={() => { isSaveAndNew.current = true; }}
                style={{
                  padding: '0.5rem 1.125rem', fontSize: '0.875rem', fontWeight: 600,
                  background: 'transparent', color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: isPending ? 'wait' : 'pointer',
                  opacity: isPending ? 0.7 : 1,
                }}
              >
                Speichern & Neu
              </button>
            )}
            <button
              type="submit"
              form="activity-form"
              disabled={isPending}
              onClick={() => { isSaveAndNew.current = false; }}
              style={{
                padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600,
                background: 'var(--color-primary)', color: '#fff',
                border: 'none', borderRadius: 'var(--radius-sm)',
                cursor: isPending ? 'wait' : 'pointer',
                opacity: isPending ? 0.7 : 1,
              }}
            >
              {isPending ? 'Speichern...' : isEditMode ? 'Speichern' : 'Aktivität speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
